// Funções auxiliares para processamento de dados do Register
import { storageService } from "../services/storageService";
import logger from "../../../utils/logger";

/**
 * Processa dados de plots recebidos da API
 * @param {Object|Array} data - Dados recebidos da API
 * @returns {Array} Array de plots processados
 */
export const processPlotsData = (data) => {
  let plotsArray = [];

  if (Array.isArray(data)) {
    plotsArray = data;
  } else if (data && typeof data === "object") {
    // Verificar se é apenas objeto de resposta (message/codigo) sem dados
    if (data.message && data.codigo && !data.dados && Object.keys(data).length <= 3) {
      logger.warn("Resposta é apenas objeto de status, sem dados de plots");
      plotsArray = [];
    } else {
      // Se for objeto, verificar se tem dados dentro
      if (data.dados && Array.isArray(data.dados)) {
        plotsArray = data.dados;
      } else if (data.dados && typeof data.dados === "object") {
        // Se dados for objeto, pode estar organizado por região
        const region = storageService.getRegion();
        if (region && data.dados[region] && Array.isArray(data.dados[region])) {
          plotsArray = data.dados[region];
        } else {
          // Tentar extrair arrays de dentro de dados
          const dadosValues = Object.values(data.dados);
          const dadosArrays = dadosValues.filter(item => Array.isArray(item));
          if (dadosArrays.length > 0) {
            plotsArray = dadosArrays.flat();
          }
        }
      } else {
        // Tentar encontrar arrays dentro do objeto principal
        const region = storageService.getRegion();
        if (region && data[region] && Array.isArray(data[region])) {
          plotsArray = data[region];
        } else {
          // Tentar encontrar qualquer array dentro do objeto
          const values = Object.values(data);
          const arrays = values.filter(item => Array.isArray(item));
          if (arrays.length > 0) {
            plotsArray = arrays.flat();
          } else {
            // Tentar pegar todos os valores que são arrays
            Object.keys(data).forEach(key => {
              if (Array.isArray(data[key])) {
                plotsArray.push(...data[key]);
              }
            });
          }
        }
      }
    }
  }

  return plotsArray;
};

/**
 * Busca um plot específico no array de plots
 * @param {Array} plots - Array de plots
 * @param {string} plotNumber - Número do plot a buscar
 * @param {string} region - Região do plot
 * @returns {Object|null} Plot encontrado ou null
 */
export const findPlot = (plots, plotNumber, region) => {
  if (!plots || !Array.isArray(plots) || plots.length === 0) {
    return null;
  }

  const found = plots.find(
    (register) =>
      String(register.plot) === String(plotNumber) &&
      register.region === region
  );

  return found || null;
};

const normalize = (value) => String(value || "").trim().toLowerCase();

export const getExactPlotMatches = (plots, plotNumber, region) => {
  if (!Array.isArray(plots) || plots.length === 0) return [];

  const targetPlot = normalize(plotNumber);
  const targetRegion = normalize(region);

  return plots.filter((item) => {
    const samePlot = normalize(item?.plot) === targetPlot;
    const sameRegion = !targetRegion || normalize(item?.region) === targetRegion;
    return samePlot && sameRegion;
  });
};

export const plotExistsInAllPlots = (plots, plotNumber, region) => {
  return getExactPlotMatches(plots, plotNumber, region).length > 0;
};

/**
 * Determina qual componente de praga usar baseado no nome da praga
 * @param {string} plagueName - Nome da praga
 * @returns {string} Tipo de componente ('caterpillar', 'beetle', 'psilideo', null)
 */
export const getPlagueComponentType = (plagueName) => {
  if (!plagueName) return null;

  // Importação dinâmica para evitar dependência circular
  const {
    CATERPILLAR_PLAGUES,
    BEETLE_PLAGUES,
    PSILIDEO_PLAGUES,
    LEPTOCYBE_PLAGUES,
  } = require("./constants");

  if (CATERPILLAR_PLAGUES.includes(plagueName)) {
    return "caterpillar";
  } else if (BEETLE_PLAGUES.includes(plagueName)) {
    return "beetle";
  } else if (PSILIDEO_PLAGUES.includes(plagueName)) {
    return "psilideo";
  } else if (LEPTOCYBE_PLAGUES.includes(plagueName)) {
    return "leptocybe";
  }

  return null;
};

